import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'
import type { FinancialStatementView, FinancialStatementViewRow } from './financialStatementModel'

const INDENT_REM = 1.25

function StatementLabel({ row }: { row: FinancialStatementViewRow }) {
  const label = row.label ?? ''
  const indentedStyle = row.level > 0 ? { paddingLeft: `${row.level * INDENT_REM}rem` } : undefined

  if (!row.tooltip) {
    return (
      <span className={cn(row.kind === 'note' && 'text-xs text-muted-foreground')} style={indentedStyle}>
        {label}
      </span>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="max-w-full text-left underline decoration-dotted underline-offset-2 hover:text-primary"
          style={indentedStyle}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm text-xs">
        {row.tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

function rowClassName(kind: FinancialStatementViewRow['kind']): string {
  switch (kind) {
    case 'section':
      return 'bg-muted/40 hover:bg-muted/40 font-semibold'
    case 'subtotal':
      return 'border-t font-semibold hover:bg-muted/30'
    case 'total':
      return 'bg-muted/70 font-semibold hover:bg-muted/70'
    case 'spacer':
      return 'hover:bg-transparent border-0'
    case 'note':
      return 'hover:bg-transparent'
    default:
      return 'hover:bg-muted/30'
  }
}

export function FinancialStatementTable({
  view,
  hideTitle = false,
}: {
  view: FinancialStatementView
  hideTitle?: boolean
}) {
  const columnCount = 1 + view.periods.length
  const showHeader = (!hideTitle && !!view.title) || !!view.subtitle || !!view.asOf

  return (
    <section className="space-y-2">
      {showHeader && (
        <div>
          {!hideTitle && <h3 className="text-sm font-semibold tracking-tight text-foreground">{view.title}</h3>}
          {view.subtitle && (
            <p className={cn('text-xs text-muted-foreground', !hideTitle && 'mt-0.5')}>{view.subtitle}</p>
          )}
          {view.asOf && <p className="text-xs text-muted-foreground">As at {view.asOf}</p>}
        </div>
      )}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 w-1/2 bg-muted/40 text-xs uppercase tracking-wide">
                <span className="sr-only">Line item</span>
              </TableHead>
              {view.periods.map((period) => (
                <TableHead
                  key={period.id}
                  className="h-10 bg-muted/40 text-right text-xs uppercase tracking-wide"
                >
                  {period.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {view.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-16 text-center text-muted-foreground">
                  No rows
                </TableCell>
              </TableRow>
            ) : (
              view.rows.map((row) => {
                if (row.kind === 'spacer') {
                  return (
                    <TableRow key={row.id} className={rowClassName(row.kind)}>
                      <TableCell colSpan={columnCount} className="h-3 p-0" />
                    </TableRow>
                  )
                }

                if (row.kind === 'note') {
                  return (
                    <TableRow key={row.id} className={rowClassName(row.kind)}>
                      <TableCell colSpan={columnCount} className="py-2">
                        <StatementLabel row={row} />
                      </TableCell>
                    </TableRow>
                  )
                }

                return (
                  <TableRow key={row.id} className={rowClassName(row.kind)}>
                    <TableCell className="py-2 text-sm">
                      <StatementLabel row={row} />
                    </TableCell>
                    {view.periods.map((period) => (
                      <TableCell key={period.id} className="py-2 text-right text-sm tabular-nums">
                        {row.values[period.id] ?? ''}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
